/**
 * Button component inspired by Park UI's design system
 */

import React from "react";
import "./Button.css";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = "primary", size = "md", children, className, style, ...props },
    ref
  ) => {
    // Get base styles
    const baseStyles: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "0.75rem",
      fontSize: "1.125rem",
      fontWeight: "600",
      fontFamily:
        "'Poppins', 'Nunito', 'Comic Neue', system-ui, -apple-system, sans-serif",
      cursor: "pointer",
      border: "none",
      transition: "all 0.2s ease",
      textDecoration: "none",
    };

    // Get size styles
    const sizeStyles: React.CSSProperties = {
      sm: { padding: "0.5rem 1rem", fontSize: "0.875rem" },
      md: { padding: "0.5rem 1.5rem", fontSize: "1.125rem" },
      lg: { padding: "0.75rem 2rem", fontSize: "1.125rem" },
    }[size];

    // Get variant styles
    const variantStyles: React.CSSProperties = {
      primary: {
        backgroundColor: "#e89b8f",
        color: "white",
        border: "none",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        opacity: 1,
      },
      secondary: {
        backgroundColor: "#5ba3c7",
        color: "white",
        border: "none",
      },
      outline: {
        backgroundColor: "transparent",
        color: "#4a3a2a",
        border: "2px solid #4a3a2a",
      },
    }[variant];

    // Merge all styles
    const buttonStyles: React.CSSProperties = {
      ...baseStyles,
      ...sizeStyles,
      ...variantStyles,
      ...style,
    };

    // Get CSS class for hover effects
    const variantClass = `button-${variant}`;
    const combinedClassName = className
      ? `${variantClass} ${className}`
      : variantClass;

    return (
      <button
        ref={ref}
        style={buttonStyles}
        className={combinedClassName}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";
